import React from 'react';
import PropTypes from 'prop-types';
import { strings } from '../../../../localization';
import ProgressOverlay from '../../../../components/common/ProgressOverlay';
import ResizeThrottler from '../../../../components/ResizeThrottler';
import { SUPPORTED_TIME_PERIOD_TYPES, colorForSafetyScoreEventType } from '../SafetyScore';
import { MAX_SCORE_TREND_PREVIOUS_PERIODS, timePeriodShortLabelForDate } from '../SafetyScoreModuleGroup';
import {
    Axis,
    Chart,
    Geom,
    Tooltip,
} from "bizcharts";
import DataSet from "@antv/data-set";
import { localizedSortEventTypeIds } from "../SafetyScore";

const IDEAL_HEIGHT_FOR_WIDTH_375 = 265 - 100; // -120 to make room for the legend
const IDEAL_TOP_PADDING_FOR_WIDTH_375 = 5;
const IDEAL_RIGHT_PADDING_FOR_WIDTH_375 = 50; // leaves enough room for angled x axis labels
const IDEAL_BOTTOM_PADDING_FOR_WIDTH_375 = 40;
const IDEAL_LEFT_PADDING_FOR_WIDTH_375 = 40;
const IDEAL_AXIS_LABEL_FONT_SIZE_FOR_WIDTH_375 = 10;
const IDEAL_MARGIN_TOP_OFFSET_FOR_WIDTH_375 = 10;

const RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS = 100;

export default class ScoreTrend extends React.PureComponent {

    static propTypes = {
        selectedTimePeriodType: PropTypes.string.isRequired,
        availableEventsFactors: PropTypes.array.isRequired,
        progressPercentageComplete: PropTypes.number,
        periodStartDate: PropTypes.instanceOf(Date),
        periodEndDate: PropTypes.instanceOf(Date),
        totalEventsByType: PropTypes.object,
        trendScoresSummaries: PropTypes.array
    };

    constructor(props) {

        super(props);

        this.chartContainerRef = React.createRef();

        const eventsFactorsWithNoZeroWeight = this.props.availableEventsFactors.filter(eventFactor => eventFactor.weight > 0);

        this.state = {
            parentDivWidth: 0,
            dynamicHeight: 0,
            eventTypeFilter: undefined,
            selectedEventTypes: localizedSortEventTypeIds(eventsFactorsWithNoZeroWeight),
        };
    };

    updateChartDimensions = () => {

        if (this.chartContainerRef.current) {

            const parentDivWidth = this.chartContainerRef.current.offsetWidth;
            const dynamicHeight = IDEAL_HEIGHT_FOR_WIDTH_375 * (parentDivWidth / 375);

            if (this.state.parentDivWidth !== parentDivWidth ||
                this.state.dynamicHeight !== dynamicHeight) {
                this.setState({
                    parentDivWidth,
                    dynamicHeight
                });
            }
        }
    };

    componentDidUpdate(prevProps) {
        if (prevProps.availableEventsFactors !== this.props.availableEventsFactors) {

            const eventsFactorsWithNoZeroWeight = this.props.availableEventsFactors.filter(eventFactor => eventFactor.weight > 0);

            this.setState({
                eventTypeFilter: undefined,
                selectedEventTypes: localizedSortEventTypeIds(eventsFactorsWithNoZeroWeight)
            });
        }
        this.updateChartDimensions();
    };

    componentDidMount() {
        this.updateChartDimensions();
    };

    typeColorJSXexpression = ["eventType", colorForSafetyScoreEventType];

    chartData = () => {

        let data = [];

        if (this.props.trendScoresSummaries && this.props.trendScoresSummaries.length > 0) {

            for (let index = this.props.trendScoresSummaries.length - 1; index >= 0; index--) {

                const trendScoreSummary = this.props.trendScoresSummaries[index];
                const trendPeriodStartDateString = timePeriodShortLabelForDate(trendScoreSummary.periodStartDate);
                const trendPeriodEndDateString = timePeriodShortLabelForDate(trendScoreSummary.periodEndDate);

                const dataItem = {
                    period: trendPeriodStartDateString + " - " + trendPeriodEndDateString,
                };
                for (const [eventType, count] of Object.entries(trendScoreSummary.totalEventsByType)) {
                    if (this.state.eventTypeFilter && this.state.eventTypeFilter !== eventType) {
                        continue;
                    }
                    dataItem[eventType] = count;
                }
                data.push(dataItem);
            }
        }
        if (this.props.totalEventsByType && this.props.periodStartDate) {

            const currentDataItem = {
                period: timePeriodShortLabelForDate(this.props.periodStartDate) + " - " + strings.score_time_period_present,
            };
            for (const [eventType, count] of Object.entries(this.props.totalEventsByType)) {
                if (this.state.eventTypeFilter && this.state.eventTypeFilter !== eventType) {
                    continue;
                }
                currentDataItem[eventType] = count;
            }
            data.push(currentDataItem);
        }
        return data;
    };

    currentIdealWidthConversionFactor = () => {
        return (this.state.parentDivWidth / 375);
    };

    bizChartsTootlipItemTemplate = '<li data-index={index}>' +
        '<span style="background-color:{color};" class="g2-tooltip-marker"></span>' +
        '{eventName}<br/>' +
        '{totalEvents}' +
        '</li>';

    bizChartsLegendContainerTemplate = '<div class="g2-tooltip">' +
        '<p class="g2-tooltip-title"></p>' +
        '<ul class="g2-tooltip-list"></ul>' +
        '</div>';


    render() {

        let scoreTrendPeriodDescription = "";
        let totalTimePeriods = 1 + MAX_SCORE_TREND_PREVIOUS_PERIODS;

        switch (this.props.selectedTimePeriodType) {
            case SUPPORTED_TIME_PERIOD_TYPES.WEEKLY:
                scoreTrendPeriodDescription = strings.formatString(strings.score_trend_last_x_weeks, totalTimePeriods);
                break;
            case SUPPORTED_TIME_PERIOD_TYPES.MONTHLY:
                scoreTrendPeriodDescription = strings.formatString(strings.score_trend_last_x_months, totalTimePeriods);
                break;
            case SUPPORTED_TIME_PERIOD_TYPES.QUARTERLY:
                scoreTrendPeriodDescription = strings.formatString(strings.score_trend_last_x_quarters, totalTimePeriods);
                break;
            default:
                console.warn("ScoreTrend unsupported time period: " + this.props.selectedTimePeriodType);
                break;
        }

        const idealWidthConversionFactor = this.currentIdealWidthConversionFactor();

        const data = this.chartData();

        const ds = new DataSet();
        const dv = ds.createView().source(data);

        dv.transform({
            type: "fold",
            fields: this.state.selectedEventTypes,
            key: "eventType",
            value: "count",
            retains: ["period"]
        });

        return (
            <section className="score-events" ref={this.chartContainerRef} >
                <header className="score-header">
                    <h6>
                        <span>
                            {strings.score_event_types_header}
                        </span>
                    </h6>
                    <span>
                        {scoreTrendPeriodDescription}
                        {this.state.eventTypeFilter &&
                            <>{" - " + document.getEventName({ type: this.state.eventTypeFilter })}</>
                        }
                    </span>
                </header>
                <div >
                    <Chart
                        width={this.state.parentDivWidth}
                        height={this.state.dynamicHeight}
                        padding={[
                            IDEAL_TOP_PADDING_FOR_WIDTH_375 * idealWidthConversionFactor,
                            IDEAL_RIGHT_PADDING_FOR_WIDTH_375 * idealWidthConversionFactor,
                            IDEAL_BOTTOM_PADDING_FOR_WIDTH_375 * idealWidthConversionFactor,
                            IDEAL_LEFT_PADDING_FOR_WIDTH_375 * idealWidthConversionFactor
                        ]}
                        data={dv}
                        forceFit
                    >
                        <Tooltip
                            showTitle={true}
                            itemTpl={this.bizChartsTootlipItemTemplate}
                            containerTpl={this.bizChartsLegendContainerTemplate}
                            shared={false}
                        />
                        <Axis
                            name="period"
                            label={{
                                textStyle: {
                                    fontSize: IDEAL_AXIS_LABEL_FONT_SIZE_FOR_WIDTH_375 * idealWidthConversionFactor
                                }
                            }}
                        />
                        <Axis
                            name="count"
                            label={{
                                textStyle: {
                                    fontSize: IDEAL_AXIS_LABEL_FONT_SIZE_FOR_WIDTH_375 * idealWidthConversionFactor
                                }
                            }}
                        />
                        <Geom
                            type="intervalStack"
                            position="period*count"
                            color={this.typeColorJSXexpression}
                            tooltip={[
                                "eventType*count",
                                (eventType, count) => {
                                    return {
                                        eventName: document.getEventName({ type: eventType }),
                                        totalEvents: count
                                    };
                                }
                            ]}
                        />
                    </Chart>
                </div>
                <div className="events-trend-summary-legend" style={{marginTop: String((IDEAL_MARGIN_TOP_OFFSET_FOR_WIDTH_375 / 2) * idealWidthConversionFactor) + "px"}}>
                    <button
                        className={this.state.eventTypeFilter === undefined ?
                            "legend-item legend-item-all-events active" :
                            "legend-item legend-item-all-events"}
                        onClick={() => this.setState({ eventTypeFilter: undefined })}
                    >
                        <div className="legend-color"></div>
                        <div className={"legend-label"}>
                            {strings.score_event_types_all_label}
                        </div>
                    </button>
                    <div className="legend-separator"></div>
                    {this.state.selectedEventTypes.map(eventType => {
                        let eventTypeHasEvents = this.props.totalEventsByType[eventType] > 0;
                        if (!eventTypeHasEvents) {
                            const firstSummaryWithEventsForEventType = this.props.trendScoresSummaries.find(trendScoreSummary => {
                                return trendScoreSummary.totalEventsByType[eventType] > 0;
                            });
                            if (firstSummaryWithEventsForEventType) {
                                eventTypeHasEvents = true;
                            }
                        }
                        return (
                            <button
                                key={eventType}
                                title={this.props.totalEventsByType[eventType]}
                                disabled={!eventTypeHasEvents}
                                className={this.state.eventTypeFilter === eventType ?
                                    "legend-item active" :
                                    "legend-item"}
                                onClick={() => this.setState({ eventTypeFilter: eventType })}>
                                <div className="legend-color" style={{backgroundColor: colorForSafetyScoreEventType(eventType)}}></div>
                                <div className="legend-label">
                                    {document.getEventName({ type: eventType })}
                                </div>
                            </button>
                        )
                    })}
                </div>
                {this.props.progressPercentageComplete !== 100 &&
                    <ProgressOverlay />
                }
                <ResizeThrottler interval={RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS} callback={this.updateChartDimensions} />
            </section>
        );
    }
}
